import tnaMitraApi from '@/api/tna-mitra/tna-mitra';
import { normalizeErrorMessage } from '@/utils/error-message';

const resolvePlanningYear = (data) => {
  const currentYear = new Date().getFullYear();
  const parsedYear = Number(
    data?.planning_year
      || data?.year
      || data?.training_year
      || data?.submitted_year
      || data?.planningYear
      || data?.tna_year
  );

  if (Number.isInteger(parsedYear) && parsedYear > 0) {
    return parsedYear;
  }

  return currentYear;
};

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Participant related state
  mandatoriedEmployees: [],
  searchedEmployees: [],
  participants: [],
  participantLoading: false,
  participantError: null,
  // Summary statistics
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  summaryLoading: false
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Participant getters
  mandatoriedEmployees: state => state.mandatoriedEmployees || [],
  searchedEmployees: state => state.searchedEmployees || [],
  participants: state => state.participants || [],
  participantLoading: state => state.participantLoading || false,
  participantError: state => state.participantError || null,
  // Summary getters
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  summaryLoading: state => state.summaryLoading || false
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Participant mutations
  SET_MANDATORIED_EMPLOYEES(state, employees) {
    state.mandatoriedEmployees = Array.isArray(employees) ? employees : [];
  },
  SET_SEARCHED_EMPLOYEES(state, employees) {
    state.searchedEmployees = Array.isArray(employees) ? employees : [];
  },
  SET_PARTICIPANTS(state, participants) {
    state.participants = Array.isArray(participants) ? participants : [];
  },
  ADD_PARTICIPANT(state, participant) {
    state.participants.push(participant);
  },
  MARK_NEW_PARTICIPANTS_SUBMITTED(state, submittedParticipants) {
    const submittedKeys = new Set(
      submittedParticipants.map(participant => {
        const nik = participant.employee_id || participant.nik;
        const year = resolvePlanningYear(participant);
        return `${nik}-${year}`;
      })
    );

    state.participants = state.participants.map(participant => {
      const nik = participant.employee_id || participant.nik;
      const year = resolvePlanningYear(participant);

      if (!submittedKeys.has(`${nik}-${year}`)) {
        return participant;
      }

      return {
        ...participant,
        tna_status: 'Waiting for PJO Approval',
        status: 'Waiting for PJO Approval',
        is_submitted: true,
        is_locked: true,
        is_new: false
      };
    });
  },
  REMOVE_PARTICIPANT(state, payload) {
    const participantId = payload?.participantId || payload;
    const planningYear = Number(payload?.planningYear);

    const index = state.participants.findIndex(p => {
      const currentParticipantId = p.id || p.employee_id || p.nik;
      const currentPlanningYear = Number(p.planning_year || p.year || p.training_year);

      if (currentParticipantId !== participantId) {
        return false;
      }

      if (Number.isInteger(planningYear) && planningYear > 0) {
        return currentPlanningYear === planningYear;
      }

      return true;
    });

    if (index !== -1) {
      state.participants.splice(index, 1);
    }
  },
  SET_PARTICIPANT_LOADING(state, loading) {
    state.participantLoading = loading;
  },
  SET_PARTICIPANT_ERROR(state, error) {
    state.participantError = error;
  },
  CLEAR_PARTICIPANT_ERROR(state) {
    state.participantError = null;
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    state.summary = summary || {
      this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
      history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
    };
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  }
};

const actions = {
  async getCompanyScopes() {
    const response = await tnaMitraApi.getCompanyScopes();
    if (response.data.httpCode === 200) {
      return response.data.data || [];
    }
    throw new Error(response.data.message || 'Gagal memuat scope perusahaan.');
  },

  async getCourses({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await tnaMitraApi.getCourses(params);

      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data || []);
        commit('SET_PAGINATION', {
          total: response.data.data.total || 0,
          page: response.data.data.page || 1,
          limit: response.data.data.limit || 10,
          totalPage: response.data.data.totalPage || 0
        });
        return response.data.data.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat daftar pelatihan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat daftar pelatihan.');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await tnaMitraApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat detail pelatihan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat detail pelatihan.');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  downloadTnaMitraTemplate: async ({ commit }, payload) => {
      // commit('SET_LOADING', true);
      try {
          const courseId = typeof payload === 'object' ? payload.courseId : payload;
          const params = typeof payload === 'object' ? payload.params : undefined;
          const response = await tnaMitraApi.downloadTnaMitraTemplate(courseId, params);
          return response;
      } catch (error) {
          console.error('Error downloading participant template:', error);
          throw error;
      }
      // finally {
      //     commit('SET_LOADING', false);
      // }
  },

  uploadTnaMitraFile: async ({ commit }, { courseId, formData, params }) => {
      commit('SET_LOADING', true);
      try {
          const response = await tnaMitraApi.uploadTnaMitraFile(courseId, formData, params);
          return response.data;
      } catch (error) {
          console.error('Error uploading participant file:', error);
          throw error;
      }
      finally {
          commit('SET_LOADING', false);
      }
  },

  // Get mandatoried employees for a course
  async getMandatoriedEmployees({ commit }, payload) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      const response = await tnaMitraApi.getMandatoriedEmployees(courseId, params);
      
      if (response.data.httpCode === 200) {
        const employees = response.data.data.map(emp => ({
          ...emp,
          id: emp.nik, // Use NIK as ID
          employee_name: emp.fullname,
          employee_id: emp.nik,
          position: emp.position_level,
          email: emp.email || '-',
          company: emp.company,
          planning_year: resolvePlanningYear(emp),
          is_mandatory: emp.is_mandatory, // Flag mandatory dari API (tidak bisa dihapus)
          is_submitted: false,
          is_locked: Boolean(emp.is_mandatory),
          tna_status: emp.status || null,
          is_new: false,
          training_mapping_id: Number(emp.training_mapping_id) || null // Preserve for submission as number
        }));
        commit('SET_MANDATORIED_EMPLOYEES', employees);
        return employees;
      } else {
        throw new Error(response.data.message || 'Gagal memuat data karyawan mandatory.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat data karyawan mandatory.');
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  // Get submitted participants for a course
  async getSubmittedParticipants({ commit }, payload) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      const response = await tnaMitraApi.getSubmittedParticipants(courseId, params);
      
      if (response.data.httpCode === 200) {
        const responsePayload = response.data.data;
        const rows = Array.isArray(responsePayload)
          ? responsePayload
          : responsePayload?.data || [];
        const employees = rows.map(emp => ({
          ...emp,
          id: emp.nik, // Use NIK as ID
          employee_name: emp.full_name,
          employee_id: emp.nik,
          position: emp.position_level,
          email: emp.email || '-',
          company: emp.company,
          planning_year: resolvePlanningYear(emp),
          is_mandatory: Boolean(emp.is_mandatory),
          is_submitted: true,
          is_locked: true,
          tna_id: emp.id,
          tna_status: emp.status || 'submitted',
          is_new: false,
          training_mapping_id: Number(emp.training_mapping_id || emp.contractor_training_mapping_id) || null // Preserve for submission as number
        }));
        return employees;
      } else {
        throw new Error(response.data.message || 'Gagal memuat daftar peserta pengajuan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat daftar peserta pengajuan.');
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  // Search employees to add as participants
  async searchEmployees({ commit }, payload) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const searchQuery = typeof payload === 'object' ? payload.search : payload;
      const params = typeof payload === 'object'
        ? { search: searchQuery, ...(payload.params || {}) }
        : { search: searchQuery };
      const response = await tnaMitraApi.searchEmployees(params);
      console.log(response.data.data);
      
      if (response.data.httpCode === 200) {
        const employees = response.data.data.data.map(emp => ({
          ...emp,
          id: emp.nik,
          employee_name: emp.fullname,
          employee_id: emp.nik,
          position: emp.position,
          email: emp.email || '-',
          company: emp.company,
          is_mandatory: false,
          is_new: false,
          training_mapping_id: 0 // Manually searched employees don't have mapping ID yet
        }));
        commit('SET_SEARCHED_EMPLOYEES', employees);
        return employees;
      } else {
        throw new Error(response.data.message || 'Gagal mencari data karyawan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal mencari data karyawan.');
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  async syncEmployeeOnDemand({ dispatch }, payload) {
    const nik = typeof payload === 'object' ? payload.nik : payload;
    const params = typeof payload === 'object' ? payload.params : undefined;
    const response = await tnaMitraApi.syncEmployeeOnDemand(nik, params);
    const result = response?.data?.data || null;

    if (result?.employee) {
      const employees = await dispatch('searchEmployees', {
        search: nik,
        params: params || {}
      });
      return { ...result, employees };
    }

    return result;
  },

  // Add participant to course (TEMPORARY - not hitting API)
  addParticipant({ commit, state }, { participant, planningYear }) {
    const selectedPlanningYear = Number(planningYear) || new Date().getFullYear();

    // Check if already exists (global across all planning years)
    const exists = state.participants.some(p => 
      p.employee_id === participant.employee_id
      || p.nik === participant.nik
      || p.id === participant.id
    );

    if (exists) {
      throw new Error('Employee already added as participant');
    }

    // Add to local state with is_mandatory: false (temporary participant)
    commit('ADD_PARTICIPANT', {
      ...participant,
      planning_year: selectedPlanningYear,
      is_mandatory: false, // Flag temporary participant (bisa dihapus)
      is_submitted: false,
      is_locked: false,
      tna_status: null,
      is_new: true,
      training_mapping_id: Number(participant.training_mapping_id) || 0 // For manually added, default to 0
    });
  },

  // Delete participant from course (LOCAL ONLY - not hitting API)
  deleteParticipant({ commit }, { participantId, planningYear }) {
    commit('REMOVE_PARTICIPANT', {
      participantId,
      planningYear
    });
  },

  // Submit only newly added participants to avoid revalidating existing rows.
  async submitParticipants({ commit, state }, payload) {
    commit('SET_PARTICIPANT_LOADING', true);
    commit('CLEAR_PARTICIPANT_ERROR');
    
    try {
      const newParticipants = state.participants.filter(p => p.is_new === true);

      if (!newParticipants.length) {
        throw new Error('Tidak ada peserta baru untuk disubmit.');
      }

      const participantPayload = newParticipants.map(p => ({
        nik: p.employee_id || p.nik,
        contractor_training_mapping_id: Number(p.training_mapping_id) || null,
        year: Number(p.planning_year || p.year || p.training_year || new Date().getFullYear()),
        is_new: true
      }));
      
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      const response = await tnaMitraApi.submitParticipants(courseId, {
        participants: participantPayload
      }, params);
      
      if (response.data.httpCode === 200 || response.data.httpCode === 201) {
        commit('MARK_NEW_PARTICIPANTS_SUBMITTED', newParticipants);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal menyimpan peserta.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal menyimpan peserta.');
      commit('SET_PARTICIPANT_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANT_LOADING', false);
    }
  },

  // Get TNA summary statistics
  async getSummary({ commit }, payload) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const tnaPeriodId = typeof payload === 'object' ? payload.tnaPeriodId : payload;
      const params = typeof payload === 'object' ? payload.params || {} : {};
      const response = await tnaMitraApi.getSummary({ tna_period: tnaPeriodId, ...params });
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat ringkasan TNA.');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      // Set empty summary on error
      commit('SET_SUMMARY', {
        this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
        history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
      });
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  // Download bulk TNA template
  async downloadBulkTemplate(_context, params) {
    try {
      const response = await tnaMitraApi.downloadBulkTemplate(params);
      return response;
    } catch (error) {
      console.error('Error downloading bulk template:', error);
      throw error;
    }
  },

  // Upload bulk TNA Excel
  async bulkUploadTna({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const formData = payload?.formData || payload;
      const params = payload?.params;
      const response = await tnaMitraApi.uploadBulkTna(formData, params);
      return response.data;
    } catch (error) {
      console.error('Error uploading bulk TNA:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
